import { getCompanyPolicyById } from "EntitiesApi/Selectors";
import { history } from "QuotePage/Store";
import { take, select, fork, call } from "redux-saga/effects";

import { appendAuthParams } from "Common/TokenAuth/Utils";

import { STEP_COMPLETED, INITIAL_STATE_LOADED, type StepCompletedAction, type StepName } from "./Actions";
import { getFirstIncompleteURL } from "./Selectors";
import {
  renewalOpenEnrollmentURL,
  renewalDocumentUploadURL,
  renewalEmployeeStatusURL,
  renewalDoneURL,
  renewalCoverageOptionsURL,
  renewalCompanyInformationURL,
} from "./urls";

const nextUrl = (step: StepName): string => {
  switch (step) {
    case "companyInformation":
      return renewalCoverageOptionsURL;
    case "coverageOptions":
      return renewalOpenEnrollmentURL;
    case "openEnrollment":
      return renewalDocumentUploadURL;
    case "documentUpload":
      return renewalEmployeeStatusURL;
  }

  throw new Error(`Unknown step: ${JSON.stringify(step)}`);
};

const shouldBypassCompanyInformationStep = (): boolean => {
  if (typeof window === "undefined") return false;

  try {
    // test-only flag, set from playwright
    return window.localStorage.getItem("e2e_skip_company_info") === "true";
  } catch {
    return false;
  }
};

export default function* navigationSaga(): Generator<any, any, any> {
  // On initial load of any employer-renewal URL, always send the employer to
  // the first incomplete step in the renewal flow.
  //
  // This ensures:
  // - If the signature on the renewal policy is missing (no signatureImageDataUrl),
  //   getFirstIncompleteURL points to the Coverage Options + Signature step.
  // - Deep links like /employer-renewal/document-upload or /employer-renewal/done
  //   can no longer bypass the signature requirement.
  yield fork(function* () {
    yield take(INITIAL_STATE_LOADED);

    const currentURL = history.location.pathname;

    // 🔧 test-only bypass: treat company-information as "completed"
    if (shouldBypassCompanyInformationStep()) {
      return;
    }

    // Only guard URLs in the employer-renewal namespace
    if (!/^\/employer-renewal(\/.*)?$/.test(currentURL)) {
      return;
    }

    const firstIncompleteURL: string = yield select(getFirstIncompleteURL);

    // Avoid unnecessary redirects when the user is already on the first incomplete step.
    if (firstIncompleteURL && firstIncompleteURL !== currentURL) {
      yield call(history.push, appendAuthParams(firstIncompleteURL));
    }
  });

  // Standard step-by-step navigation once the user completes a step.
  while (true) {
    const action: StepCompletedAction = yield take(STEP_COMPLETED);
    const companyPolicyId = yield select((state) => state.companyPolicyId);
    const companyPolicy = yield select((state) => getCompanyPolicyById(state, companyPolicyId));

    let url = nextUrl(action.payload);

    // If we're not changing carrier, we can skip the document upload step.
    if (!companyPolicy.changingCarrier && url === renewalDocumentUploadURL) {
      url = renewalEmployeeStatusURL;
    }

    // If open enrollment is explicitly skipped, jump straight to "done".
    if (companyPolicy.skipOpenEnrollment === true && (url === renewalDocumentUploadURL || url === renewalEmployeeStatusURL)) {
      url = renewalDoneURL;
    }
    yield call(history.push, url);
  }
}
